import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getSubscriptionStatus } from '@/services/api';
import { 
  CheckCircle2, 
  Loader2,
  ArrowRight,
  XCircle
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const result = await getSubscriptionStatus(sessionId);
      
      if (result.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
      } else if (result.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Failed to check status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="text-center">
            {status === 'checking' && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="w-16 h-16 text-emerald-500 animate-spin" />
                </div>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Vérification du paiement...
                </CardTitle>
                <CardDescription>
                  Veuillez patienter pendant que nous confirmons votre abonnement
                </CardDescription>
              </>
            )}
            
            {status === 'success' && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="flex justify-center mb-4"
                >
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                  </div>
                </motion.div>
                <CardTitle className="text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Paiement réussi !
                </CardTitle>
                <CardDescription>
                  Votre abonnement est maintenant actif. Merci pour votre confiance !
                </CardDescription>
              </>
            )}
            
            {(status === 'expired' || status === 'timeout') && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-12 h-12 text-red-500" />
                  </div>
                </div>
                <CardTitle className="text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {status === 'expired' ? 'Session expirée' : 'Délai dépassé'}
                </CardTitle>
                <CardDescription>
                  {status === 'expired' 
                    ? 'La session de paiement a expiré. Veuillez réessayer.'
                    : 'La vérification a pris trop de temps. Vérifiez votre email pour confirmation.'
                  }
                </CardDescription>
              </>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            {status === 'success' && (
              <div className="space-y-3">
                <Button 
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-to-dashboard-btn"
                >
                  Accéder au dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
            
            {(status === 'expired' || status === 'timeout') && (
              <div className="space-y-3">
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => navigate('/subscription')}
                  data-testid="retry-subscription-btn"
                >
                  Réessayer
                </Button>
                <Button 
                  className="w-full"
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                >
                  Retour au dashboard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
